import { IndicatorDTOHack, SloInstanceDto } from '@atlassiansox/phobos-client-ts';

import { cohortTypeSorted } from './slo-panel/types';
import { CohortMemberCohortTypeEnum, SLOIndicatorWithGroupType } from './types';
import { NON_COHORT_PREFIX } from './constants';

const resolveCohortValue = (cohort: any, cohortTypes: string[] = []): string => {
  // For dynamic SLO Groups, we might have cohort types with empty cohort value.
  // In that case, we will resolve the cohort value by cohort type hierarchy.
  // The resolution logic is: if cohort value exists, return it; if not, return
  // "non-" + cohort type. However, if the cohort type is the root level cohort
  // (cohortTypes[0]), we will just return cohort type string to avoid
  // confusion. e.g. global -> 'global' (instead of "non-global")
  if (cohort.value) return cohort.value;
  if (cohortTypes[0] === cohort.cohort) return cohort.cohort;
  if (cohort.cohort) return `${NON_COHORT_PREFIX}${cohort.cohort}`;
  return '';
};

export const formatCohortName = (cohort: string): string =>
  cohort
    .toLowerCase()
    .replace('aws', 'AWS') // it might or might not even have aws prefix
    .replace('_', ' ')
    .replace('-', ' ');

export const getIndicatorDynamicRollupCohorts = (
  indicator: any,
  cohortTypeSortedDynamic: string[] = []
): string[] => {
  const rollupCohorts: string[] = [];
  const cohorts = indicator?.cohorts || [];
  // We wanted to exclude the first level of cohort hierarchy because it's the
  // root cohort which is not considered as rollup cohort and it makes lots of
  // duplicated data in the table rows rollup cohort column.
  cohortTypeSortedDynamic.slice(1).some((type: string) => {
    if (type === indicator.groupType) return true;
    const cohort = cohorts.find((x: any) => x.cohort === type) || {};
    const cohortName = resolveCohortValue(cohort, cohortTypeSortedDynamic);
    if (cohortName) rollupCohorts.push(cohortName);
    return false;
  });
  return rollupCohorts;
};

// Legacy endpoint & mimir rollup data handling
export const getIndicatorRollupCohorts = (indicator: any): string[] => {
  const rollupCohorts: string[] = [];
  cohortTypeSorted.slice(1).some((type: string) => {
    if (type === indicator.groupType) return true;
    const cohortName = indicator[type?.toLowerCase()] || '';
    if (cohortName) rollupCohorts.push(cohortName);
    return false;
  });
  const cohorts =
    indicator?.groupType === CohortMemberCohortTypeEnum.Global
      ? ['-']
      : [CohortMemberCohortTypeEnum.Global.toLowerCase(), ...rollupCohorts];
  return cohorts;
};

// Mimir endpoint data handling
export const getSloInstanceCohortMemberName = (instance: any): string =>
  instance?.cohortType === CohortMemberCohortTypeEnum.Global
    ? CohortMemberCohortTypeEnum.Global
    : instance.cohortMember[instance.cohortType];

// Legacy endpoint data handling
export const getIndicatorCohortMemberName = (indicator: any): string =>
  indicator?.groupType === CohortMemberCohortTypeEnum.Global
    ? CohortMemberCohortTypeEnum.Global
    : indicator[indicator.groupType?.toLowerCase()];

export const getIndicatorDynamicCohortMemberName = (
  indicator: any,
  cohortTypeSortedDynamic: string[] = []
): string => {
  const cohort = indicator?.cohorts?.find((x: any) => x.cohort === indicator.groupType) ?? {};
  return resolveCohortValue(cohort, cohortTypeSortedDynamic);
};

// Deprecated function, only used for legacy slo group member page. Will be
// removed once allowDynamicSLOGroup FF is removed.
export const getIndicatorCohortMemberTagName = (
  indicator: SLOIndicatorWithGroupType | undefined
): string => {
  if (!indicator) return '';
  return indicator?.groupType === CohortMemberCohortTypeEnum.Global
    ? CohortMemberCohortTypeEnum.Global
    : `${indicator.groupType}:${
        (indicator as any).cohorts?.length > 0
          ? getIndicatorDynamicCohortMemberName(indicator)
          : getIndicatorCohortMemberName(indicator)
      }`;
};

export const getDynamicCohortMemberTagName = (
  indicator: SLOIndicatorWithGroupType | undefined,
  cohortTypeSortedDynamic: string[] = []
): string => {
  if (!indicator) return '';
  const memberName = getIndicatorDynamicCohortMemberName(indicator, cohortTypeSortedDynamic);
  if (memberName.toLowerCase() === indicator.groupType?.toLowerCase()) return memberName;
  return `${indicator.groupType}:${memberName}`;
};

export const getMimirCohortMemberTagName = (sloInstance: SloInstanceDto | undefined): string => {
  if (!sloInstance) return '';
  return sloInstance.cohortType === CohortMemberCohortTypeEnum.Global
    ? CohortMemberCohortTypeEnum.Global
    : `${sloInstance.cohortType}:${(sloInstance.cohortMember ?? {})[sloInstance.cohortType ?? '']}`;
};

export const isIndicatorTypeMimir = (indicator: IndicatorDTOHack | undefined): boolean =>
  indicator?.type === (('EXTERNAL' as unknown) as IndicatorDTOHack['type']);
